import {View} from '@fantasmagames/slot/src/common/view/View';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpriteButton} from '@fantasmagames/core/src/display/SpriteButton';
import {NineSliceSpriteButton} from '@fantasmagames/core/src/display/NineSliceSpriteButton';
import {FreeGamesData, FreeGamesInProgress} from '@fantasmagames/slot/src/logic/integration/FreeGamesData';
import {NineSlicePlane} from '@fantasmagames/core/src/display/NineSlicePlane';
import {ReelConstants} from '@fantasmagames/slot/src/modules/reels/ReelConstants';

export class FreeRoundsScreenView extends View {
	public static readonly PLAY_NOW_PRESSED: string = 'PLAY_NOW_PRESSED';
	public static readonly PLAY_LATER_PRESSED: string = 'PLAY_LATER_PRESSED';
	public static readonly INFO_PRESSED: string = 'INFO_PRESSED';
	public static readonly DISCARD_PRESSED: string = 'DISCARD_PRESSED';
	public static readonly NEXT_OFFER_PRESSED: string = 'NEXT_OFFER_PRESSED';
	public static readonly BACK_TO_GAME_PRESSED: string = 'BACK_TO_GAME_PRESSED';
	public static readonly RESUME_PRESSED: string = 'RESUME_PRESSED';
	public static readonly KEEP_IT_PRESSED: string = 'KEEP_IT_PRESSED';
	public static readonly DISCARD_FINAL_PRESSED: string = 'DISCARD_FINAL_PRESSED';
	public static readonly EXPIRY_CONTINUE_PRESSED: string = 'EXPIRY_CONTINUE_PRESSED';

	public static readonly SCREEN_SHOWN: string = 'SCREEN_SHOWN';
	public static readonly SCREEN_HIDDEN: string = 'SCREEN_HIDDEN';

	// texts
	protected _congratulationText: PIXI.Text;
	protected _congratulationsTextStyle: PIXI.TextStyle;
	protected _youHaveText: PIXI.Text;
	protected _youHaveTextStyle: PIXI.TextStyle;
	protected _freeGamesNumberText: PIXI.Text;
	protected _freeGamesTextStyle: PIXI.TextStyle;
	protected _infoText: PIXI.Text;
	protected _infoTextStyle: PIXI.TextStyle;
	protected _sesionIDText: PIXI.Text;
	protected _sesionIDStyle: PIXI.TextStyle;
	protected _summaryGameInfoText: PIXI.Text;
	protected _summaryGameInfoStyle: PIXI.TextStyle;
	protected _freeGamesCompltedWinText: PIXI.Text;
	protected _freeGamesCompltedWinStyle: PIXI.TextStyle;
	protected _summaryYouHaveWonText: PIXI.Text;
	protected _summaryYouHaveWonStyle: PIXI.TextStyle;
	protected _summaryTotalWinText: PIXI.Text;
	protected _summaryTotalWinStyle: PIXI.TextStyle;
	protected _freeGamesCompletedNoWinText: PIXI.Text;
	protected _freeGamesCompletedNoWinStyle: PIXI.TextStyle;
	protected _noMoreOffersText: PIXI.Text;
	protected _noMoreOffersStyle: PIXI.TextStyle;
	protected _resumeYouHaveText: PIXI.Text;
	protected _resumeYouHaveStyle: PIXI.TextStyle;
	protected _resumeFreeGamesNumberText: PIXI.Text;
	protected _resumeFreeGamesNumberStyle: PIXI.TextStyle;
	protected _resumeRemainingText: PIXI.Text;
	protected _resumeRemainingStyle: PIXI.TextStyle;

	protected _discardCaptionText: PIXI.Text;
	protected _discardCaptionStyle: PIXI.TextStyle;
	protected _discardDescriptionText: PIXI.Text;
	protected _discardDescriptionStyle: PIXI.TextStyle;

	protected _expiryCaptionText: PIXI.Text;
	protected _expiryCaptionStyle: PIXI.TextStyle;
	protected _expiryCaptionInfoText: PIXI.Text;
	protected _expiryCaptionInfoStyle: PIXI.TextStyle;

	// buttons
	protected _leftButton: NineSliceSpriteButton;
	protected _middleButton: NineSliceSpriteButton;
	protected _rightButton: NineSliceSpriteButton;
	protected _leftButtonText: PIXI.Text;
	protected _middleButtonText: PIXI.Text;
	protected _rightButtonText: PIXI.Text;
	protected _infoButton: SpriteButton;
	protected _discardButton: SpriteButton;
	protected _buttonTextStyle: PIXI.TextStyle;

	protected static BUTTONS_MIN_WIDTH: number = 220;
	protected static BUTTONS_MIN_HEIGHT: number = 110;

	protected _mainAnimation: SpineAnimation;

	protected _currentAnimation: Animation;

	public init(): void {
		super.init();

		this.container.interactive = true;
	}

	public hide(): void {
		super.hide();

		this.emit(FreeRoundsScreenView.SCREEN_HIDDEN);
	}

	public showStartscreen(freeGamesData: FreeGamesData): void {
		this.show();
		this.showMenuAnimation('STARTSCREEN_IDLE', false);

		this._leftButton.callback = this.onPlayNowPressed.bind(this);
		this._rightButton.callback = this.onPlayLaterPressed.bind(this);

		this._discardButton.callback = this.onDiscardPressed.bind(this);
		this._infoButton.callback = this.onInfoPressed.bind(this);

		this.disableButon(this._middleButton);

		const congratulationsText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_congratulations_uppercase') || 'CONGRATULATIONS!';
		this._congratulationText.text = congratulationsText;
		this._youHaveText.text = '';

		let freeGamesNumberText: string = '';
		if (freeGamesData.gamesAwarded === 1) {
			freeGamesNumberText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_youhavefreegames_sg') ||
				`YOU HAVE ${freeGamesData.gamesAwarded} FREE GAME`;
		} else {
			freeGamesNumberText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_youhavefreegames_pl') ||
				`YOU HAVE ${freeGamesData.gamesAwarded} FREE GAMES`;
		}
		freeGamesNumberText = freeGamesNumberText.replace('{0}', `\n${freeGamesData.gamesAwarded}`);
		this._freeGamesNumberText.text = freeGamesNumberText;

		const sessionIDText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_id_info') || `ID ${freeGamesData.offerID}`;
		this._sesionIDText.text = sessionIDText.replace('{0} - {1}', `${freeGamesData.offerID} - ${freeGamesData.instanceID}`);

		this._leftButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_play_now') || 'Play Now';
		this._rightButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_play_later') || 'Play Later';

		this.emit(FreeRoundsScreenView.SCREEN_SHOWN);
	}

	public showEndScreen(lastFreeGame: FreeGamesInProgress, creditType: number, hasNextOffer: boolean): void {
		this.show();
		let animationName: string = '';
		if (lastFreeGame.sumWinnings > 0 && hasNextOffer) {
			animationName = 'SUMMARY_SCREEN_02';
		} else if (lastFreeGame.sumWinnings > 0 && !hasNextOffer) {
			animationName = 'SUMMARY_SCREEN_01';
		} else if (lastFreeGame.sumWinnings === 0 && hasNextOffer) {
			animationName = 'SUMMARY_SCREEN_03';
		} else if (lastFreeGame.sumWinnings === 0 && !hasNextOffer) {
			animationName = 'SUMMARY_SCREEN_04';
		}
		this.showMenuAnimation(animationName, false);

		if (hasNextOffer) {
			this._leftButton.callback = this.onBackToTheGame.bind(this);
			this._rightButton.callback = this.onNextOffer.bind(this);

			this._leftButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_backtogame') || 'Back To Game';
			this._rightButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_next_offer') || 'Next Offer';

			this.disableButon(this._middleButton);
			this.disableButon(this._discardButton);
			this.disableButon(this._infoButton);
		} else {
			this._middleButton.callback = this.onBackToTheGame.bind(this);

			this._middleButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_backtogame') || 'Back To Game';

			this.disableButon(this._leftButton);
			this.disableButon(this._rightButton);
			this.disableButon(this._discardButton);
			this.disableButon(this._infoButton);
		}

		if (lastFreeGame.sumWinnings > 0) {
			this._congratulationText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_congratulations_uppercase') || 'CONGRATULATIONS!';
			this._freeGamesCompltedWinText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamescomplete_uppercase') || 'FREE GAMES COMPLETE';
			this._summaryYouHaveWonText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_youhavewon') || 'You Have Won';
			this._summaryTotalWinText.text = `${(window as any).V.API.Core.convertCurrency(lastFreeGame.sumWinnings)}`;

			if (creditType === 0) {
				this._summaryGameInfoText.text =
					this.translator.getObject('BaseStrings', 'generic', 'g_freegames_addedto_cashbalance_info') ||
					'Winnings added to Cash balance';
			} else if (creditType === 1) {
				this._summaryGameInfoText.text =
					this.translator.getObject('BaseStrings', 'generic', 'g_freegames_addedto_bonusbalance_info') ||
					'Winnings added to Bonus balance';
			} else {
				this._summaryGameInfoText.text =
					this.translator.getObject('BaseStrings', 'generic', 'g_freegames_alladdedtobalance') ||
					'All winnings from those Free Games that were completed have already been added to your balance.';
			}
		} else {
			this._freeGamesCompletedNoWinText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamescomplete_uppercase') || 'FREE GAMES COMPLETE';
			this._noMoreOffersText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_no_offers_available') ||
				'There are no more offers available';
			this._summaryGameInfoText.text = ``;
		}

		this.emit(FreeRoundsScreenView.SCREEN_SHOWN);
	}

	public showRestoreScreen(lastFreeGame: FreeGamesInProgress, canDiscard: boolean): void {
		this.show();
		this.showMenuAnimation('RESUME_SCREEN_IDLE', false);

		this._middleButton.callback = this.onResumeFreeGamesPressed.bind(this);
		this._middleButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_resume_play') || 'Resume Play';

		if (canDiscard) {
			this._discardButton.visible = true;
			this._discardButton.callback = this.onDiscardPressed.bind(this);
		} else {
			this._discardButton.callback = null;
			this._discardButton.visible = false;
			this.disableButon(this._discardButton);
		}
		this._infoButton.callback = this.onInfoPressed.bind(this);

		this._resumeRemainingText.text = '';
		this._resumeYouHaveText.text = '';
		let freeGamesNumberText: string = '';
		if (lastFreeGame.gamesRemaining === 0) {
			freeGamesNumberText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamesremaining') || `YOU HAVE FREE GAMES REMAINING`;
		} else if (lastFreeGame.gamesRemaining === 1) {
			freeGamesNumberText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamesremaining_sg') ||
				`YOU HAVE ${lastFreeGame.gamesRemaining} FREE GAME`;
		} else {
			freeGamesNumberText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamesremaining_pl') ||
				`YOU HAVE ${lastFreeGame.gamesRemaining} FREE GAMES`;
		}
		freeGamesNumberText = freeGamesNumberText.replace('{0}', `\n${lastFreeGame.gamesRemaining}`);
		this._resumeFreeGamesNumberText.text = freeGamesNumberText;

		const sessionIDText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_id_info') || `ID ${lastFreeGame.offerID}`;
		this._sesionIDText.text = sessionIDText.replace('{0} - {1}', `${lastFreeGame.offerID} - ${lastFreeGame.instanceID}`);

		this.disableButon(this._leftButton);
		this.disableButon(this._rightButton);

		this.emit(FreeRoundsScreenView.SCREEN_SHOWN);
	}

	public showDiscardQuestionsScreen(freeGamesData: FreeGamesData): void {
		this.show();
		this.showMenuAnimation('STARTSCREEN_DISCARD', false);

		this._leftButton.callback = this.onKeepIt.bind(this);
		this._rightButton.callback = this.onDiscardFinal.bind(this);

		this._leftButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_keep_it') || 'Keep It';
		this._rightButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_discard') || 'Discard';

		this._discardCaptionText.text =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_discard_offer_uppercase') || 'DISCARD OFFER';
		if (
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_discard_confirmation') &&
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_action_cannotbe_undone')
		) {
			this._discardDescriptionText.text =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_discard_confirmation') +
				'\n' +
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_action_cannotbe_undone');
		} else {
			this._discardDescriptionText.text = 'Are you sure you want to discard the offer now?\nThis action cannot be undone.';
		}

		const sessionIDText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_id_info') || `ID ${freeGamesData.offerID}`;
		this._sesionIDText.text = sessionIDText.replace('{0} - {1}', `${freeGamesData.offerID} - ${freeGamesData.instanceID}`);

		this.disableButon(this._middleButton);
		this.disableButon(this._discardButton);
		this.disableButon(this._infoButton);

		this.emit(FreeRoundsScreenView.SCREEN_SHOWN);
	}

	public showExpiryScreen(instanceID: string, offerID: string): void {
		this.show();
		this.showMenuAnimation('EXPIRY_SCREEN_IDLE', false);

		this._middleButton.callback = this.onExpiryContinuePressed.bind(this);
		this._middleButtonText.text = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_continue') || 'Continue';

		this._expiryCaptionInfoText.text =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_alladdedtobalance') ||
			`All winnings from those Free Games that were completed have already been added to your balance`;

		const sessionExpiredText: string =
			this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamessession_expired') ||
			`FREE GAMES SESSION ID ${offerID} - ${instanceID} HAS EXPIRED`;
		this._expiryCaptionText.text = sessionExpiredText.replace('{0} - {1}', `${offerID} - ${instanceID}`);

		this.disableButon(this._leftButton);
		this.disableButon(this._rightButton);
		this.disableButon(this._discardButton);
		this.disableButon(this._infoButton);

		this.emit(FreeRoundsScreenView.SCREEN_SHOWN);
	}

	public showInfoText(freeGamesData: FreeGamesData, hasLines: boolean = false, isBothWays: boolean = false): void {
		let expiryText: string;
		if (this.translator.getObject('BaseStrings', 'generic', 'g_freegames_expiry_info')) {
			expiryText = this.translator
				.getObject('BaseStrings', 'generic', 'g_freegames_expiry_info')
				.replace('{0}', `${freeGamesData.endDate}`);
		} else {
			expiryText = `Expiry: ${freeGamesData.endDate}`;
		}
		let betInfoText: string = '';
		// Bet Info: {50} Lines, {ChipSize} Coins @ {TotalBet/50 lines}
		if (freeGamesData.betData.chipSize === 1) {
			if (this.translator.getObject('BaseStrings', 'generic', 'g_freegames_bet_info_sg')) {
				betInfoText = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_bet_info_sg');
				if (hasLines) {
					const betValue: string = isBothWays ? `${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * ReelConstants.LINES * 2)}` :
						`${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * ReelConstants.LINES)}`;
					betInfoText = betInfoText
						.replace('{0}', `${ReelConstants.LINES}`)
						.replace('{1}', `${freeGamesData.betData.numChips * freeGamesData.betData.chipSize}`)
						.replace('{2}', `${betValue}`);
				} else {
					const posOf0: number = betInfoText.indexOf('{0}');
					const lineToReplace: string = betInfoText.substr(posOf0, betInfoText.indexOf('{1}') - posOf0) + '{1}';
					betInfoText = betInfoText
						.replace(lineToReplace, `${freeGamesData.betData.numChips * freeGamesData.betData.chipSize}`)
						.replace('{2}', `${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * 50)}`);
				}
			} else {
				betInfoText = `Bet Info: ${freeGamesData.betData.numChips} Coin @ ${(window as any).V.API.Core.convertCurrency(
					freeGamesData.betData.chipSize * 50,
				)}`;
			}
		} else {
			if (this.translator.getObject('BaseStrings', 'generic', 'g_freegames_bet_info_pl')) {
				betInfoText = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_bet_info_pl');
				if (hasLines) {
					const betValue: string = isBothWays ? `${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * ReelConstants.LINES * 2)}` :
						`${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * ReelConstants.LINES)}`;

					betInfoText = betInfoText
						.replace('{0}', `${ReelConstants.LINES}`)
						.replace('{1}', `${freeGamesData.betData.numChips * freeGamesData.betData.chipSize}`)
						.replace('{2}', `${betValue}`);
				} else {
					const posOf0: number = betInfoText.indexOf('{0}');
					const lineToReplace: string = betInfoText.substr(posOf0, betInfoText.indexOf('{1}') - posOf0) + '{1}';
					betInfoText = betInfoText
						.replace(lineToReplace, `${freeGamesData.betData.numChips * freeGamesData.betData.chipSize}`)
						.replace('{2}', `${(window as any).V.API.Core.convertCurrency(freeGamesData.betData.chipSize * 50)}`);
				}
			} else {
				betInfoText = `Bet Info: ${freeGamesData.betData.numChips * freeGamesData.betData.chipSize} Coin @ ${(window as any).V.API.Core.convertCurrency(
					freeGamesData.betData.chipSize * 50,
				)}`;
			}
		}

		let winningsAddedText: string;
		// creditType === 0 - cash balance;
		// creditType === 1 - bonus balance;
		if (freeGamesData.creditType === 0) {
			winningsAddedText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_addedto_cashbalance_info') ||
				'Winnings added to Cash balance';
		} else {
			winningsAddedText =
				this.translator.getObject('BaseStrings', 'generic', 'g_freegames_addedto_bonusbalance_info') ||
				'Winnings added to Bonus balance';
		}
		this._infoText.text = `${expiryText}\n${betInfoText}\n${winningsAddedText}`;
	}

	public hideInfoText(): void {
		this._infoText.text = '';
	}

	protected setupElements(screenSpine: Spine): void {
		this._infoButton = new SpriteButton();
		this._discardButton = new SpriteButton();

		this._leftButton = new NineSliceSpriteButton();
		this._middleButton = new NineSliceSpriteButton();
		this._rightButton = new NineSliceSpriteButton();

		this._congratulationsTextStyle = this.textStyles.freeRoundsCaptionStyle;
		this._youHaveTextStyle = this.textStyles.freeRoundsTextStyle;
		this._freeGamesTextStyle = this.textStyles.freeRoundsTextStyle;
		this._infoTextStyle = this.textStyles.freeRoundsSmallTextStyle;
		this._sesionIDStyle = this.textStyles.freeRoundsSuperSmallTextStyle;
		this._summaryGameInfoStyle = this.textStyles.freeRoundsTextStyle;
		this._freeGamesCompltedWinStyle = this.textStyles.freeRoundsTextStyle;
		this._summaryYouHaveWonStyle = this.textStyles.freeRoundsTextStyle;
		this._summaryTotalWinStyle = this.textStyles.freeRoundsTextStyle;
		this._freeGamesCompletedNoWinStyle = this.textStyles.freeRoundsTextStyle;
		this._noMoreOffersStyle = this.textStyles.freeRoundsTextStyle;
		this._resumeYouHaveStyle = this.textStyles.freeRoundsTextStyle;
		this._resumeFreeGamesNumberStyle = this.textStyles.freeRoundsTextStyle;
		this._resumeRemainingStyle = this.textStyles.freeRoundsTextStyle;
		this._discardCaptionStyle = this.textStyles.freeRoundsCaptionStyle;
		this._discardDescriptionStyle = this.textStyles.freeRoundsTextStyle;
		this._expiryCaptionStyle = this.textStyles.freeRoundsCaptionStyle;
		this._expiryCaptionInfoStyle = this.textStyles.freeRoundsCaptionStyle;

		this._congratulationText = new PIXI.Text('CoNgRaTuLaTiOnS', this._congratulationsTextStyle);
		this._congratulationText.anchor.set(0.5);
		this._youHaveText = new PIXI.Text('youHaveText', this._youHaveTextStyle);
		this._youHaveText.anchor.set(0.5);
		this._freeGamesNumberText = new PIXI.Text('N Free GaMeZZz', this._freeGamesTextStyle);
		this._freeGamesNumberText.anchor.set(0.5);
		this._freeGamesNumberText.style.align = 'center';
		this._infoText = new PIXI.Text('', this._infoTextStyle);
		this._infoText.anchor.set(0.5);
		this._sesionIDText = new PIXI.Text('session ID 0123456789', this._sesionIDStyle);
		this._sesionIDText.anchor.set(0.5);
		this._summaryGameInfoText = new PIXI.Text('summary info', this._summaryGameInfoStyle);
		this._summaryGameInfoText.anchor.set(0.5);

		this._freeGamesCompltedWinText = new PIXI.Text('', this._freeGamesCompltedWinStyle);
		this._freeGamesCompltedWinText.anchor.set(0.5);
		this._summaryYouHaveWonText = new PIXI.Text('', this._summaryYouHaveWonStyle);
		this._summaryYouHaveWonText.anchor.set(0.5);
		this._summaryTotalWinText = new PIXI.Text('', this._summaryTotalWinStyle);
		this._summaryTotalWinText.anchor.set(0.5);
		this._freeGamesCompletedNoWinText = new PIXI.Text('', this._freeGamesCompletedNoWinStyle);
		this._freeGamesCompletedNoWinText.anchor.set(0.5);
		this._noMoreOffersText = new PIXI.Text('', this._noMoreOffersStyle);
		this._noMoreOffersText.anchor.set(0.5);

		this._resumeYouHaveText = new PIXI.Text('', this._resumeYouHaveStyle);
		this._resumeYouHaveText.anchor.set(0.5);
		this._resumeFreeGamesNumberText = new PIXI.Text('', this._resumeFreeGamesNumberStyle);
		this._resumeFreeGamesNumberText.anchor.set(0.5);
		this._resumeFreeGamesNumberText.style.align = 'center';
		this._resumeRemainingText = new PIXI.Text('', this._resumeRemainingStyle);
		this._resumeRemainingText.anchor.set(0.5);

		this._discardCaptionText = new PIXI.Text('', this._discardCaptionStyle);
		this._discardCaptionText.anchor.set(0.5);
		this._discardDescriptionText = new PIXI.Text('', this._discardDescriptionStyle);
		this._discardDescriptionText.anchor.set(0.5);

		this._expiryCaptionText = new PIXI.Text('', this._expiryCaptionStyle);
		this._expiryCaptionText.anchor.set(0.5);
		this._expiryCaptionInfoText = new PIXI.Text('', this._expiryCaptionInfoStyle);
		this._expiryCaptionInfoText.anchor.set(0.5);

		this._buttonTextStyle = this.textStyles.freeRoundsButtonTextStyle;
		this._buttonTextStyle.wordWrap = true;
		this._buttonTextStyle.wordWrapWidth = FreeRoundsScreenView.BUTTONS_MIN_WIDTH - 20;
		////////////////////////////////////////////////////////////////////
		// left Button
		////////////////////////////////////////////////////////////////////
		const freeGamesMenuButton = {
			texture: this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_MENU_BUTTONS.png'),
			nine: this.resources.getNineSliceData('MICROGAMING.json', 'FREEGAMES_MENU_BUTTONS.png'),
		};

		const freeGamesMenuButtonClicked = {
			texture: this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_MENU_BUTTONS_CLICKED.png'),
			nine: this.resources.getNineSliceData('MICROGAMING.json', 'FREEGAMES_MENU_BUTTONS_CLICKED.png'),
		};

		const leftButtonUp: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const leftButtonDown: NineSlicePlane = new NineSlicePlane(freeGamesMenuButtonClicked.texture, freeGamesMenuButtonClicked.nine);
		const leftButtonOver: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const leftButtonDisabled: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);

		this._leftButton.setFrames(leftButtonUp, leftButtonDown, leftButtonOver, leftButtonDisabled);
		this._leftButton.setSize({
			width: FreeRoundsScreenView.BUTTONS_MIN_WIDTH,
			height: FreeRoundsScreenView.BUTTONS_MIN_HEIGHT,
		});

		this._leftButtonText = new PIXI.Text('Left Button', this._buttonTextStyle);
		this._leftButtonText.anchor.set(0.5);

		////////////////////////////////////////////////////////////////////
		// Middle Button
		////////////////////////////////////////////////////////////////////
		const middleButtonUp: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const middleButtonDown: NineSlicePlane = new NineSlicePlane(freeGamesMenuButtonClicked.texture, freeGamesMenuButtonClicked.nine);
		const middleButtonOver: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const middleButtonDisabled: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);

		this._middleButton.setFrames(middleButtonUp, middleButtonDown, middleButtonOver, middleButtonDisabled);
		this._middleButton.setSize({
			width: FreeRoundsScreenView.BUTTONS_MIN_WIDTH,
			height: FreeRoundsScreenView.BUTTONS_MIN_HEIGHT,
		});

		this._middleButtonText = new PIXI.Text('Middle Button', this._buttonTextStyle);
		this._middleButtonText.anchor.set(0.5);

		////////////////////////////////////////////////////////////////////
		// Right Button
		////////////////////////////////////////////////////////////////////
		const rightButtonUp: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const rightButtonDown: NineSlicePlane = new NineSlicePlane(freeGamesMenuButtonClicked.texture, freeGamesMenuButtonClicked.nine);
		const rightButtonOver: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);
		const rightButtonDisabled: NineSlicePlane = new NineSlicePlane(freeGamesMenuButton.texture, freeGamesMenuButton.nine);

		this._rightButton.setFrames(rightButtonUp, rightButtonDown, rightButtonOver, rightButtonDisabled);
		this._rightButton.setSize({
			width: FreeRoundsScreenView.BUTTONS_MIN_WIDTH,
			height: FreeRoundsScreenView.BUTTONS_MIN_HEIGHT,
		});

		this._rightButtonText = new PIXI.Text('Right Button', this._buttonTextStyle);
		this._rightButtonText.anchor.set(0.5);

		////////////////////////////////////////////////////////////////////
		// Info Button
		////////////////////////////////////////////////////////////////////
		const infoButtonUp: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_INFO_BUTTON.png'),
		);
		const infoButtonDown: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_INFO_BUTTON_CLICKED.png'),
		);
		const infoButtonOver: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_INFO_BUTTON.png'),
		);
		const infoButtonDisabled: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_INFO_BUTTON.png'),
		);

		infoButtonUp.anchor.set(0.5, 0.5);
		infoButtonDown.anchor.set(0.5, 0.5);
		infoButtonOver.anchor.set(0.5, 0.5);
		infoButtonDisabled.anchor.set(0.5, 0.5);

		this._infoButton.setFrames(infoButtonUp, infoButtonDown, infoButtonOver, infoButtonDisabled);

		////////////////////////////////////////////////////////////////////
		// Discard Button
		////////////////////////////////////////////////////////////////////
		const discardButtonUp: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'),
		);
		const discardButtonDown: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON_CLICKED.png'),
		);
		const discardButtonOver: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'),
		);
		const discardButtonDisabled: PIXI.Sprite = new PIXI.Sprite(
			this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'),
		);

		discardButtonUp.anchor.set(0.5, 0.5);
		discardButtonDown.anchor.set(0.5, 0.5);
		discardButtonOver.anchor.set(0.5, 0.5);
		discardButtonDisabled.anchor.set(0.5, 0.5);

		this._discardButton.setFrames(discardButtonUp, discardButtonDown, discardButtonOver, discardButtonDisabled);

		const leftButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('BUTTON_BG01')];
		const centerButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('BUTTON_BG03')];
		const rightButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('BUTTON_BG02')];
		const infoButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('INFO_BUTTON')];
		const discardButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('DISCARD_BUTTON')];

		const leftButtonTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('TEXT01')];
		const centerButtonTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('TEXT03')];
		const rightButtonTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('TEXT02')];

		const congratulationsTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('CONGRATULATIONS_TEXT')];
		const youHaveTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('STARTSCREEN_YOU_HAVE_TEXT')];
		const winTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('STARTSCREEN_X_FREE_GAMES_TEXT')];
		const infoTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('INFO_TEXT')];
		const sessionTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SESSION_IDENTIFICATION_NUMBER')];
		const summaryInfoSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_GAME_INFORMATION')];

		const freeGameCompleteWinTextSlot =
			screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_FREE_GAMES_COMPLETE_WIN_TEXT')];
		const summaryYouHaveWonTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_YOU_HAVE_WON_TEXT')];
		const summaryWinNumberTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_WIN_NUMBER_TEXT')];
		const summaryFreeGamesCompletedTextSlot =
			screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_FREE_GAMES_COMPLETE_NO_WIN_TEXT')];
		const summaryNoMoreOffersTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('SUMMARY_NO_MORE_OFFERS_TEXT')];

		const resumeYouHaveTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('RESUME_SCREEN_YOU_HAVE_TEXT')];
		const resumeFreeGamesNumberTextSlot =
			screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('RESUME_SCREEN_X_FREE_GAMES_TEXT')];
		const resumeRemainingTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('RESUME_SCREEN_REMAINING_TEXT')];

		const discardCaptionTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('DISCARD_DISCARD_OFFER_TEXT')];
		const discardTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('DISCARD_DISCARD_OFFER_TEXT02')];

		const expiryCaptionTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('EXPIRY_SCREEN_EXPIRED_TEXT')];
		const expiryTextInfoSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('EXPIRY_SCREEN_EXPIRED_INFO_TEXT')];

		expiryCaptionTextSlot.addChild(this._expiryCaptionText);
		this._expiryCaptionText.transform = expiryCaptionTextSlot.children[0].transform;
		expiryCaptionTextSlot.children[0].visible = false;

		expiryTextInfoSlot.addChild(this._expiryCaptionInfoText);
		this._expiryCaptionInfoText.transform = expiryTextInfoSlot.children[0].transform;
		expiryTextInfoSlot.children[0].visible = false;

		discardCaptionTextSlot.addChild(this._discardCaptionText);
		this._discardCaptionText.transform = discardCaptionTextSlot.children[0].transform;
		discardCaptionTextSlot.children[0].visible = false;

		discardTextSlot.addChild(this._discardDescriptionText);
		this._discardDescriptionText.transform = discardTextSlot.children[0].transform;
		discardTextSlot.children[0].visible = false;

		resumeYouHaveTextSlot.addChild(this._resumeYouHaveText);
		this._resumeYouHaveText.transform = resumeYouHaveTextSlot.children[0].transform;
		resumeYouHaveTextSlot.children[0].visible = false;

		resumeFreeGamesNumberTextSlot.addChild(this._resumeFreeGamesNumberText);
		this._resumeFreeGamesNumberText.transform = resumeFreeGamesNumberTextSlot.children[0].transform;
		resumeFreeGamesNumberTextSlot.children[0].visible = false;

		resumeRemainingTextSlot.addChild(this._resumeRemainingText);
		this._resumeRemainingText.transform = resumeRemainingTextSlot.children[0].transform;
		resumeRemainingTextSlot.children[0].visible = false;

		freeGameCompleteWinTextSlot.addChild(this._freeGamesCompltedWinText);
		this._freeGamesCompltedWinText.transform = freeGameCompleteWinTextSlot.children[0].transform;
		freeGameCompleteWinTextSlot.children[0].visible = false;

		summaryYouHaveWonTextSlot.addChild(this._summaryYouHaveWonText);
		this._summaryYouHaveWonText.transform = summaryYouHaveWonTextSlot.children[0].transform;
		summaryYouHaveWonTextSlot.children[0].visible = false;

		summaryWinNumberTextSlot.addChild(this._summaryTotalWinText);
		this._summaryTotalWinText.transform = summaryWinNumberTextSlot.children[0].transform;
		summaryWinNumberTextSlot.children[0].visible = false;

		summaryFreeGamesCompletedTextSlot.addChild(this._freeGamesCompletedNoWinText);
		this._freeGamesCompletedNoWinText.transform = summaryFreeGamesCompletedTextSlot.children[0].transform;
		summaryFreeGamesCompletedTextSlot.children[0].visible = false;

		summaryNoMoreOffersTextSlot.addChild(this._noMoreOffersText);
		this._noMoreOffersText.transform = summaryNoMoreOffersTextSlot.children[0].transform;
		summaryNoMoreOffersTextSlot.children[0].visible = false;

		congratulationsTextSlot.addChild(this._congratulationText);
		this._congratulationText.transform = congratulationsTextSlot.children[0].transform;
		congratulationsTextSlot.children[0].visible = false;

		youHaveTextSlot.addChild(this._youHaveText);
		this._youHaveText.transform = youHaveTextSlot.children[0].transform;
		youHaveTextSlot.children[0].visible = false;

		winTextSlot.addChild(this._freeGamesNumberText);
		this._freeGamesNumberText.transform = winTextSlot.children[0].transform;
		winTextSlot.children[0].visible = false;

		infoTextSlot.addChild(this._infoText);
		this._infoText.transform = infoTextSlot.children[0].transform;
		infoTextSlot.children[0].visible = false;

		sessionTextSlot.addChild(this._sesionIDText);
		this._sesionIDText.transform = sessionTextSlot.children[0].transform;
		sessionTextSlot.children[0].visible = false;

		summaryInfoSlot.addChild(this._summaryGameInfoText);
		this._summaryGameInfoText.transform = summaryInfoSlot.children[0].transform;
		summaryInfoSlot.children[0].visible = false;

		leftButtonSlot.addChild(this._leftButton);
		this._leftButton.transform = leftButtonSlot.children[0].transform;
		leftButtonSlot.children[0].visible = false;

		leftButtonTextSlot.addChild(this._leftButtonText);
		this._leftButtonText.transform = leftButtonTextSlot.children[0].transform;
		leftButtonTextSlot.children[0].visible = false;

		centerButtonSlot.addChild(this._middleButton);
		this._middleButton.transform = centerButtonSlot.children[0].transform;
		centerButtonSlot.children[0].visible = false;

		centerButtonTextSlot.addChild(this._middleButtonText);
		this._middleButtonText.transform = centerButtonTextSlot.children[0].transform;
		centerButtonTextSlot.children[0].visible = false;

		rightButtonSlot.addChild(this._rightButton);
		this._rightButton.transform = rightButtonSlot.children[0].transform;
		rightButtonSlot.children[0].visible = false;

		rightButtonTextSlot.addChild(this._rightButtonText);
		this._rightButtonText.transform = rightButtonTextSlot.children[0].transform;
		rightButtonTextSlot.children[0].visible = false;

		infoButtonSlot.addChild(this._infoButton);
		this._infoButton.transform = infoButtonSlot.children[0].transform;
		infoButtonSlot.children[0].visible = false;

		discardButtonSlot.addChild(this._discardButton);
		this._discardButton.transform = discardButtonSlot.children[0].transform;
		discardButtonSlot.children[0].visible = false;
	}

	protected onExpiryContinuePressed(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.EXPIRY_CONTINUE_PRESSED);
	}

	protected onKeepIt(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.KEEP_IT_PRESSED);
	}

	protected onDiscardFinal(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.DISCARD_FINAL_PRESSED);
	}

	protected onResumeFreeGamesPressed(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.RESUME_PRESSED);
	}

	protected onPlayNowPressed(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.PLAY_NOW_PRESSED);
	}

	protected onPlayLaterPressed(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.PLAY_LATER_PRESSED);
	}

	protected onInfoPressed(): void {
		this.emit(FreeRoundsScreenView.INFO_PRESSED);
	}

	protected onBackToTheGame(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.BACK_TO_GAME_PRESSED);
	}

	protected onNextOffer(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.NEXT_OFFER_PRESSED);
	}

	protected onDiscardPressed(): void {
		this.hide();
		this.emit(FreeRoundsScreenView.DISCARD_PRESSED);
	}

	protected shouldReplaceLogoElement(): boolean {
		return this.resources.getLocale().toLowerCase().indexOf('zh') > -1;
	}

	protected getLocale(): string {
		const locale = this.resources.getLocale().toUpperCase();

		return locale.indexOf('ZH') > -1 ? locale.slice(0, 2) : locale;
	}

	protected showMenuAnimation(animationName: string, loop: boolean, stopOnComplete: boolean = true): SpineAnimation {
		if (this._mainAnimation) {
			this.spineUtils.stopAndDestroyAnimation(this._mainAnimation);
		}

		const spine: Spine = new Spine(this.resources.getSpine('FREE-GAMES_SCREENS.json'));
		this.setupElements(spine);
		this._mainAnimation = new SpineAnimation(spine, animationName, loop, true);
		this._mainAnimation.container.position.set(
			this.resolutionManager.halfResolution.width,
			this.resolutionManager.halfResolution.height,
		);

		if (this.shouldReplaceLogoElement()) {
			this.spineUtils.replaceElementInSpine(
				spine,
				'GAME_LOGOTYPE',
				new PIXI.Sprite(this.resources.getTexture(`${this.getLocale()}_INTRO_LOGO.png`)),
			);
		}

		this.container.addChild(this._mainAnimation.container);
		if (stopOnComplete) {
			this._mainAnimation.setOnComplete(() => {
				this._mainAnimation.stop();
			});
		}

		this.updateManager.add(this._mainAnimation);
		this._mainAnimation.start();

		return this._mainAnimation;
	}

	protected disableButon(button: SpriteButton): void {
		button.interactive = false;
		button.buttonMode = false;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenView.ts